import React, {
	forwardRef,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from 'react';
import { Platform, StyleSheet, View } from 'react-native';

// Import logo (bundled asset)
const warehouseNavigatorLogo = require('../../../../assets/warehouse_navigator.png');

export type SparcFrequencyLabel = 'Daily' | 'Weekly' | 'Period';

export type SparcChartDataPoint = {
	date: string;
	value: number | null;
};

export type SparcKpiPrintCard = {
	id: string;
	title: string;
	badgeLetter: string;
	badgeColor: string;
	actual: number | null;
	target: number | null;
	delta: number | null;
	invertDeltaColor?: boolean;
	chartData: SparcChartDataPoint[];
	isFavorite?: boolean;
};

export interface SparcKpiListPrintRef {
	print: () => void;
	printOnePerPage: () => void;
	getHtml?: () => string;
}

// -----------------------------
// Pure utilities (no side effects)
// -----------------------------
const escapeHtml = (text: string): string => {
	const div = typeof document !== 'undefined' ? document.createElement('div') : null;
	if (div) {
		div.textContent = String(text ?? '');
		return div.innerHTML;
	}

	return String(text ?? '').replace(/[&<>"']/g, (m) =>
		(
			{
				'&': '&amp;',
				'<': '&lt;',
				'>': '&gt;',
				'"': '&quot;',
				"'": '&#39;',
			} as const
		)[m] || m,
	);
};

const normalizeFrequencyLabel = (label: string): SparcFrequencyLabel => {
	const key = String(label || '').trim().toLowerCase();
	if (key === 'weekly') return 'Weekly';
	if (key === 'period') return 'Period';
	return 'Daily';
};

const formatNumber = (value: number | null): string => {
	const num = Number(value);
	if (value == null || !Number.isFinite(num)) return '--';
	return num.toFixed(2);
};

const getOrderedChartData = (
	chartData: ReadonlyArray<SparcChartDataPoint>,
): SparcChartDataPoint[] => {
	if (!chartData || chartData.length === 0) return [];
	const parsed = chartData.map((d, i) => ({ ...d, _ts: Date.parse(d.date), _idx: i }));
	const allValid = parsed.every((p) => !isNaN(p._ts));
	if (allValid) {
		return parsed
			.sort((a, b) => a._ts - b._ts)
			.map(({ _ts, _idx, ...rest }) => rest);
	}
	return [...chartData].reverse();
};

const renderStyles = (): string => {
	return `
		@page { margin: 10mm; }

		html, body { padding: 0; margin: 0; }
		body {
			font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
			color: #1C1C1C;
			background: #FFFFFF;
		}

		* { -webkit-print-color-adjust: exact; print-color-adjust: exact; box-sizing: border-box; }

		table.report-table { width: 100%; border-collapse: collapse; }
		thead { display: table-header-group; }
		tfoot { display: table-footer-group; }
		tr { page-break-inside: avoid; break-inside: avoid; }

		.print-header-cell {
			padding: 0 0 10px 0;
			border-bottom: 0;
			text-align: left;
			font-weight: normal;
		}

		.header-bar {
			display: flex;
			align-items: center;
			justify-content: space-between;
			border: 1px solid #DADADA;
			border-radius: 6px;
			padding: 8px 12px;
			background: #FFFFFF;
		}
		.header-left { display: flex; align-items: center; gap: 8px; min-width: 0; }
		.logo { width: 20px; height: 20px; object-fit: contain; border-radius: 4px; flex-shrink: 0; }
		.header-title { font-weight: 700; font-size: 16px; color: #093F6E; white-space: nowrap; }
		.header-date { font-size: 13px; font-weight: 400; color: #6B6B6B; white-space: nowrap; }

		.section-row {
			display: flex;
			align-items: center;
			justify-content: space-between;
			padding: 10px 0 12px;
			/* border-bottom: 1px solid #E6E6E6; */
		}
		.section-title { font-weight: 700; font-size: 14px; letter-spacing: 0.2px; }
		.frequency-label { font-weight: 700; font-size: 12px; color: #1C1C1C; }

		tr.cards-row { page-break-inside: avoid; break-inside: avoid; }
		td.cards-cell { padding: 12px 0 0; }

		.kpi-row {
			display: flex;
			gap: 12px;
			width: 100%;
			page-break-inside: avoid;
			break-inside: avoid;
		}

		.kpi-card {
			flex: 1;
			background: #FFFFFF;
			border-radius: 16px;
			border: 1px solid #E0E7FF;
			padding: 16px;
			page-break-inside: avoid;
			break-inside: avoid;
		}
		.kpi-card.placeholder { visibility: hidden; }

		.card-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
		.card-header-left { display: flex; align-items: center; gap: 12px; min-width: 0; }
		.badge { width: 24px; height: 24px; border-radius: 4px; display: flex; align-items: center; justify-content: center; }
		.badge-text { font-size: 14px; font-weight: 700; color: #1B5E20; }
		.card-title { font-size: 16px; font-weight: 500; color: #2D2D2D; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
		.favorite-box { width: 24px; height: 24px; border-radius: 4px; border: 1px solid #E0E0E0; display: flex; align-items: center; justify-content: center; }

		.metrics {
			display: flex;
			justify-content: space-around;
			border-radius: 16px;
			border: 1px solid #E3F2FD;
			padding: 12px 2px;
			margin-bottom: 10px;
		}
		.metric { flex: 1; text-align: center; }
		.metric-label { font-size: 12px; font-weight: 400; color: #4B4B4B; margin-bottom: 8px; }
		.metric-value { font-size: 16px; font-weight: 700; color: #000000; }
		.delta {
			display: inline-flex;
			align-items: center;
			gap: 4px;
		}
		.delta-red { color: #D32F2F; }
		.delta-green { color: rgb(18, 159, 20); }

		.chart { display: flex; gap: 8px; }
		.y-axis { display: flex; flex-direction: column; justify-content: space-between; align-items: flex-end; margin-top: -6px; margin-bottom: 22px; }
		.y-label { font-size: 12px; font-weight: 400; color: #616161; }

		.chart-content { flex: 1; }
		.bars {
			display: flex;
			align-items: flex-end;
			height: 72px;
			border-bottom: 1px solid #E0E0E0;
			gap: 4px;
		}
		.bar-wrapper { flex: 1; min-width: 20px; display: flex; justify-content: center; align-items: flex-end; height: 100%; }
		.bar {
			width: 55%;
			min-height: 2px;
			background: #41B8E6;
			border-top-left-radius: 4px;
			border-top-right-radius: 4px;
		}
		.x-axis { display: flex; gap: 4px; margin-top: 8px; }
		.x-label-wrapper { flex: 1; min-width: 20px; text-align: center; }
		.x-label { font-size: 12px; font-weight: 400; color: #616161; }

		.chart-legend { margin-top: 6px; display: flex; align-items: center; gap: 6px; }
		.legend-swatch { width: 10px; height: 10px; border-radius: 2px; background: #41B8E6; }
		.legend-text { font-size: 10px; color: #616161; }
	`;
};

// -----------------------------
// Static SVG strings (avoid re-creating templates)
// -----------------------------
const STAR_SVG_FILLED = `
	<svg width="14" height="13" viewBox="0 0 14 13" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path d="M8.54 4.41333L13.3333 4.82667L9.7 7.98L10.7867 12.6667L6.66667 10.18L2.54667 12.6667L3.64 7.98L0 4.82667L4.79333 4.42L6.66667 0L8.54 4.41333Z" fill="#3A3A3A"/>
	</svg>
`;

const STAR_SVG_OUTLINE = `
	<svg width="14" height="13" viewBox="0 0 14 13" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path d="M8.54 4.41333L13.3333 4.82667L9.7 7.98L10.7867 12.6667L6.66667 10.18L2.54667 12.6667L3.64 7.98L0 4.82667L4.79333 4.42L6.66667 0L8.54 4.41333Z" fill="none" stroke="#616161" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/>
	</svg>
`;

const renderStarSvg = (filled: boolean = false): string =>
	filled ? STAR_SVG_FILLED : STAR_SVG_OUTLINE;

const renderDeltaArrowSvg = (delta: number, isBad: boolean): string => {
	const deltaColor = isBad ? '#C12C01' : 'rgb(18, 159, 20)';
	if (delta < 0) {
		return `
		<svg width="5" height="5" viewBox="0 0 5 3" fill="none" xmlns="http://www.w3.org/2000/svg">
			<path d="M0 0L2.5 2.5L5 0H0Z" fill="${deltaColor}"/>
		</svg>
		`;
	}
	if (delta > 0) {
		return `
		<svg width="5" height="5" viewBox="0 0 5 3" fill="none" xmlns="http://www.w3.org/2000/svg">
			<path d="M5 2.5L2.5 0L0 2.5H5Z" fill="${deltaColor}"/>
		</svg>
		`;
	}
	return '';
};

const renderPrintHeader = (opts: { date: string; logoBase64?: string }): string => {
	const logoImg = opts.logoBase64
		? `<img class="logo" src="${opts.logoBase64}" alt="Warehouse Navigator" />`
		: `<div class="logo" style="background: transparent;"></div>`;

	return `
		<tr>
			<th class="print-header-cell">
				<div class="header-bar">
					<div class="header-left">
						${logoImg}
						<span class="header-title">Warehouse Navigator</span>
					</div>
					<span class="header-date">${escapeHtml(opts.date)}</span>
				</div>
			</th>
		</tr>
	`;
};

const renderSectionHeader = (opts: { title: string; frequencyLabel: SparcFrequencyLabel }): string => {
	return `
		<tr>
			<th style="padding:0; text-align:left; font-weight: normal;">
				<div class="section-row">
					<span class="section-title">${escapeHtml(opts.title)}</span>
					<span class="frequency-label">${escapeHtml(opts.frequencyLabel)}</span>
				</div>
			</th>
		</tr>
	`;
};

const renderCardHeader = (card: SparcKpiPrintCard): string => {
	return `
		<div class="card-header">
			<div class="card-header-left">
				<div class="badge" style="background:${escapeHtml(card.badgeColor || '#D4F4DD')}">
					<span class="badge-text">${escapeHtml(card.badgeLetter || '')}</span>
				</div>
				<span class="card-title">${escapeHtml(card.title || '')}</span>
			</div>
			<div class="favorite-box">${renderStarSvg(card.isFavorite || false)}</div>
		</div>
	`;
};

const renderMetrics = (card: SparcKpiPrintCard): string => {
	const hasDelta = card.delta != null;
	const isDeltaNegative = hasDelta && card.delta! < 0;
	const isDeltaPositive = hasDelta && card.delta! > 0;
	const isDeltaBad = card.invertDeltaColor ? isDeltaPositive : isDeltaNegative;
	const isDeltaGood = card.invertDeltaColor ? isDeltaNegative : isDeltaPositive;
	return `
		<div class="metrics">
			<div class="metric">
				<div class="metric-label">Actual</div>
				<div class="metric-value">${formatNumber(card.actual)}</div>
			</div>
			<div class="metric">
				<div class="metric-label">Target</div>
				<div class="metric-value">${formatNumber(card.target)}</div>
			</div>
			<div class="metric">
				<div class="metric-label">Delta</div>
				<div class="delta">
					${hasDelta ? renderDeltaArrowSvg(card.delta!, isDeltaBad) : ''}
					<span class="metric-value ${isDeltaBad ? 'delta-red' : ''} ${isDeltaGood ? 'delta-green' : ''}">${formatNumber(card.delta)}</span>
				</div>
			</div>
		</div>
	`;
};

const renderChart = (
	ordered: ReadonlyArray<SparcChartDataPoint>,
	maxValue: number,
): string => {
	const barsParts: string[] = [];
	const xLabelParts: string[] = [];

	for (const dp of ordered) {
		const pct = maxValue > 0 ? (Number(dp.value) / maxValue) * 100 : 0;
		const safePct = Number.isFinite(pct) ? Math.max(0, pct) : 0;
		barsParts.push(`
			<div class="bar-wrapper">
				<div class="bar" style="height: ${safePct.toFixed(2)}%;"></div>
			</div>
		`);
		xLabelParts.push(
			`<div class="x-label-wrapper"><span class="x-label">${escapeHtml(dp.date)}</span></div>`,
		);
	}

	const maxLabel = Number.isInteger(maxValue) ? String(maxValue) : maxValue.toFixed(2);
	return `
		<div class="chart">
			<div class="y-axis">
				<span class="y-label">${maxLabel}</span>
				<span class="y-label">0</span>
			</div>
			<div class="chart-content">
				<div class="bars">${barsParts.join('')}</div>
				<div class="x-axis">${xLabelParts.join('')}</div>
			</div>
		</div>
	`;
};

const renderKpiCard = (card: SparcKpiPrintCard): string => {
	const ordered = getOrderedChartData(card.chartData || []);

	let maxValue = 10;
	for (const dp of ordered) {
		const v = dp.value == null ? 0 : dp.value;
		if (v > maxValue) maxValue = v;
	}

	return `
		<div class="kpi-card">
			${renderCardHeader(card)}
			${renderMetrics(card)}
			${renderChart(ordered, maxValue)}
		</div>
	`;
};

const renderKpiGrid = (sparcCards: SparcKpiPrintCard[]): string => {
	const rows: SparcKpiPrintCard[][] = [];
	for (let i = 0; i < sparcCards.length; i += 3) {
		rows.push(sparcCards.slice(i, i + 3));
	}

	const rowHtml = rows
		.map((row) => {
			const placeholdersNeeded = Math.max(0, 3 - row.length);
			const cardsHtml = row.map(renderKpiCard).join('');
			const placeholders = Array.from({ length: placeholdersNeeded })
				.map(() => `<div class="kpi-card placeholder"></div>`)
				.join('');

			return `
				<tr class="cards-row">
					<td class="cards-cell">
						<div class="kpi-row">
							${cardsHtml}
							${placeholders}
						</div>
					</td>
				</tr>
			`;
		})
		.join('');

	return `<tbody>${rowHtml}</tbody>`;
};

const renderOnePerPageStyles = (): string => {
	return `
		@page { margin: 10mm; }

		html, body { padding: 0; margin: 0; }
		body {
			font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
			color: #1C1C1C;
			background: #FFFFFF;
		}
		* { -webkit-print-color-adjust: exact; print-color-adjust: exact; box-sizing: border-box; }

		.opp-page {
			display: flex;
			flex-direction: column;
			padding: 0;
			page-break-after: always;
			break-after: page;
		}
		.opp-page:last-child {
			page-break-after: avoid;
			break-after: avoid;
		}
		.opp-card-center {
			flex: 1;
			display: flex;
			align-items: center;
		}

		.header-bar {
			display: flex;
			align-items: center;
			justify-content: space-between;
			border: 1px solid #DADADA;
			border-radius: 6px;
			padding: 8px 12px;
			background: #FFFFFF;
			margin-bottom: 16px;
		}
		.header-left { display: flex; align-items: center; gap: 8px; }
		.logo { width: 20px; height: 20px; object-fit: contain; border-radius: 4px; flex-shrink: 0; }
		.header-title { font-weight: 700; font-size: 16px; color: #093F6E; }
		.header-date { font-size: 13px; font-weight: 400; color: #6B6B6B; }

		.section-row {
			display: flex;
			align-items: center;
			justify-content: space-between;
			padding: 0 0 16px;
		}
		.section-title { font-weight: 700; font-size: 14px; letter-spacing: 0.2px; }
		.frequency-label { font-weight: 700; font-size: 12px; color: #1C1C1C; }

		.kpi-card-full {
			width: 100%;
			background: #FFFFFF;
			border-radius: 16px;
			border: 1px solid #E0E7FF;
			padding: 32px;
			display: flex;
			flex-direction: column;
		}

		.card-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 24px; }
		.card-header-left { display: flex; align-items: center; gap: 16px; }
		.badge { width: 36px; height: 36px; border-radius: 6px; display: flex; align-items: center; justify-content: center; }
		.badge-text { font-size: 20px; font-weight: 700; color: #1B5E20; }
		.card-title { font-size: 24px; font-weight: 600; color: #2D2D2D; }
		.favorite-box { width: 32px; height: 32px; border-radius: 6px; border: 1px solid #E0E0E0; display: flex; align-items: center; justify-content: center; }

		.metrics {
			display: flex;
			justify-content: space-around;
			border-radius: 16px;
			border: 1px solid #E3F2FD;
			padding: 24px 8px;
			margin-bottom: 32px;
		}
		.metric { flex: 1; text-align: center; }
		.metric-label { font-size: 16px; font-weight: 400; color: #4B4B4B; margin-bottom: 12px; }
		.metric-value { font-size: 28px; font-weight: 700; color: #000000; }
		.delta { display: inline-flex; align-items: center; gap: 6px; }
		.delta-red { color: #D32F2F; }
		.delta-green { color: rgb(18, 159, 20); }

		.chart { display: flex; gap: 12px; flex: 1; }
		.y-axis { display: flex; flex-direction: column; justify-content: space-between; align-items: flex-end; margin-top: -6px; margin-bottom: 48px; }
		.y-label { font-size: 13px; font-weight: 400; color: #616161; }

		.chart-content { flex: 1; position: relative; }
		.bars {
			display: flex;
			align-items: flex-end;
			aspect-ratio: 5 / 2;
			height: auto;
			width: 100%;
			position: relative;
			border-bottom: 1px solid #E0E0E0;
			gap: 6px;
		}
		.bar-wrapper { flex: 1; display: flex; justify-content: center; align-items: flex-end; height: 100%; }
		.bar {
			width: 60%;
			min-height: 2px;
			background: #41B8E6;
			border-top-left-radius: 6px;
			border-top-right-radius: 6px;
		}
		.x-axis { display: flex; gap: 6px; margin-top: 12px; }
		.x-label-wrapper { flex: 1; text-align: center; }
		.x-label { font-size: 13px; font-weight: 400; color: #616161; }

		.chart-legend { margin-top: 16px; display: flex; align-items: center; gap: 8px; }
		.legend-swatch { width: 12px; height: 12px; border-radius: 2px; background: #41B8E6; }
		.legend-text { font-size: 12px; color: #616161; }

		/* Orientation-specific adjustments for one-per-page print */
		@media print and (orientation: portrait) {
			.opp-page { padding: 10mm; }
			.kpi-card-full { width: 100%; max-width: calc(210mm - 20mm); }
			.header-bar { width: 100%; }
		}

		@media print and (orientation: landscape) {
			.opp-page { padding: 8mm; box-sizing: border-box; }
			.opp-card-center { align-items: flex-start; min-height: 0; }
			.kpi-card-full {
				width: 100%;
				max-width: calc(297mm - 16mm);
				box-sizing: border-box;
				page-break-inside: avoid;
				break-inside: avoid;
				-webkit-column-break-inside: avoid;
				padding: 6mm;
				display: flex;
				flex-direction: column;
			}
			.header-bar { width: 100%; margin-bottom: 6px; padding: 6px 8px; }
			.card-header { margin-bottom: 6px; }
			.badge { width: 26px; height: 26px; }
			.badge-text { font-size: 13px; }
			.card-title { font-size: 16px; }
			.metrics { padding: 6px 6px; margin-bottom: 6px; gap: 8px; }
			.metric-label { font-size: 12px; margin-bottom: 4px; }
			.metric-value { font-size: 16px; }
			.chart { flex: 1; }
			.bars { gap: 4px; }
			.y-axis { margin-bottom: 18px; }
			.kpi-card-full, .opp-page, .opp-card-center { page-break-inside: avoid; break-inside: avoid; -webkit-column-break-inside: avoid; }
		}
	`;
};

const renderOnePerPageCard = (
	card: SparcKpiPrintCard,
	date: string,
	frequencyLabel: SparcFrequencyLabel,
	logoBase64: string,
): string => {
	const logoImg = logoBase64
		? `<img class="logo" src="${logoBase64}" alt="Warehouse Navigator" />`
		: `<div class="logo" style="background: transparent;"></div>`;

	const ordered = getOrderedChartData(card.chartData || []);
	let maxValue = 10;
	for (const dp of ordered) {
		const v = Number(dp.value) || 0;
		if (v > maxValue) maxValue = v;
	}

	const barsParts: string[] = [];
	const xLabelParts: string[] = [];
	for (const dp of ordered) {
		const pct = dp.value != null && maxValue > 0 ? (dp.value / maxValue) * 100 : 0;
		const safePct = Number.isFinite(pct) ? Math.max(0, pct) : 0;
		barsParts.push(`<div class="bar-wrapper">${dp.value == null ? '' : `<div class="bar" style="height: ${safePct.toFixed(2)}%;"></div>`}</div>`);
		xLabelParts.push(`<div class="x-label-wrapper"><span class="x-label">${escapeHtml(dp.date)}</span></div>`);
	}

	const hasDelta = card.delta != null;
	const isDeltaNegative = hasDelta && card.delta! < 0;
	const isDeltaPositive = hasDelta && card.delta! > 0;
	const isDeltaBad = card.invertDeltaColor ? isDeltaPositive : isDeltaNegative;
	const isDeltaGood = card.invertDeltaColor ? isDeltaNegative : isDeltaPositive;
	const maxLabel = Number.isInteger(maxValue) ? String(maxValue) : maxValue.toFixed(2);

	return `
		<div class="opp-page">
			<div class="header-bar">
				<div class="header-left">
					${logoImg}
					<span class="header-title">Warehouse Navigator</span>
				</div>
				<span class="header-date">${escapeHtml(date)}</span>
			</div>
			<div class="section-row">
				<span class="section-title">SPARC KPIs</span>
				<span class="frequency-label">${escapeHtml(frequencyLabel)}</span>
			</div>
			<div class="opp-card-center">
			<div class="kpi-card-full">
				<div class="card-header">
					<div class="card-header-left">
						<div class="badge" style="background:${escapeHtml(card.badgeColor || '#D4F4DD')}">
							<span class="badge-text">${escapeHtml(card.badgeLetter || '')}</span>
						</div>
						<span class="card-title">${escapeHtml(card.title || '')}</span>
					</div>
					<div class="favorite-box">${renderStarSvg(card.isFavorite || false)}</div>
				</div>
				<div class="metrics">
					<div class="metric">
						<div class="metric-label">Actual</div>
						<div class="metric-value">${formatNumber(card.actual)}</div>
					</div>
					<div class="metric">
						<div class="metric-label">Target</div>
						<div class="metric-value">${formatNumber(card.target)}</div>
					</div>
					<div class="metric">
						<div class="metric-label">Delta</div>
						<div class="delta">
							${hasDelta ? renderDeltaArrowSvg(card.delta!, isDeltaBad) : ''}
							<span class="metric-value ${isDeltaBad ? 'delta-red' : ''} ${isDeltaGood ? 'delta-green' : ''}">${formatNumber(card.delta)}</span>
						</div>
					</div>
				</div>
				<div class="chart">
					<div class="y-axis">
						<span class="y-label">${maxLabel}</span>
						<span class="y-label">0</span>
					</div>
					<div class="chart-content">
						<div class="bars">${barsParts.join('')}</div>
						<div class="x-axis">${xLabelParts.join('')}</div>
						<div class="chart-legend">
							<div class="legend-swatch"></div>
							<span class="legend-text">${escapeHtml(card.title)}</span>
						</div>
					</div>
				</div>
			</div>
			</div>
		</div>
	`;
};

export const generateSparcKpiOnePerPageHTML = (args: {
	date: string;
	logoBase64?: string;
	sparcCards: SparcKpiPrintCard[];
	frequencyLabel: SparcFrequencyLabel;
}): string => {
	const pagesHtml = (args.sparcCards || [])
		.map((card) =>
			renderOnePerPageCard(card, args.date, args.frequencyLabel, args.logoBase64 || ''),
		)
		.join('');

	return `<!DOCTYPE html>
<html lang="en">
	<head>
		<meta charset="UTF-8" />
		<meta name="viewport" content="width=device-width, initial-scale=1.0" />
		<title>SPARC KPIs - One Per Page</title>
		<style>
			${renderOnePerPageStyles()}
		</style>
	</head>
	<body>
		${pagesHtml}
	</body>
</html>`;
};

export const generateSparcKpiPrintHTML = (args: {
	date: string;
	logoBase64?: string;
	sparcCards: SparcKpiPrintCard[];
	frequencyLabel: SparcFrequencyLabel;
}): string => {
	const headerHtml = `
		<thead>
			${renderPrintHeader({ date: args.date, logoBase64: args.logoBase64 })}
			${renderSectionHeader({ title: 'SPARC KPIs', frequencyLabel: args.frequencyLabel })}
		</thead>
	`;

	const gridHtml = renderKpiGrid(args.sparcCards || []);

	return `<!DOCTYPE html>
<html lang="en">
	<head>
		<meta charset="UTF-8" />
		<meta name="viewport" content="width=device-width, initial-scale=1.0" />
		<title>SPARC KPIs Print</title>
		<style>
			${renderStyles()}
		</style>
	</head>
	<body>
		<table class="report-table">
			${headerHtml}
			${gridHtml}
		</table>
	</body>
</html>`;
};

type SparcKpiListPrintProps = {
	sparcCards: SparcKpiPrintCard[];
	frequencyLabel: SparcFrequencyLabel | string;
};

const SparcKpiListPrint = forwardRef(function SparcKpiListPrint(
	props: SparcKpiListPrintProps,
	ref: React.Ref<SparcKpiListPrintRef>,
) {
	const iframeRef = useRef<HTMLIFrameElement | null>(null);
	const iframeOnePerPageRef = useRef<HTMLIFrameElement | null>(null);
	const [logoBase64, setLogoBase64] = useState<string>('');

	useEffect(() => {
		const loadLogo = async () => {
			if (Platform.OS !== 'web') return;
			try {
				const response = await fetch(warehouseNavigatorLogo);
				const blob = await response.blob();
				const reader = new FileReader();
				reader.onloadend = () => setLogoBase64(reader.result as string);
				reader.readAsDataURL(blob);
			} catch (e) {
				console.warn('Failed to load logo for print:', e);
				setLogoBase64('');
			}
		};
		loadLogo();
	}, []);

	const printDate = useMemo(
		() =>
			new Date().toLocaleDateString('en-US', {
				year: 'numeric',
				month: 'short',
				day: 'numeric',
			}),
		[],
	);

	const normalizedFrequency = useMemo(
		() => normalizeFrequencyLabel(String(props.frequencyLabel || 'Daily')),
		[props.frequencyLabel],
	);

	const htmlContent = useMemo(
		() =>
			generateSparcKpiPrintHTML({
				date: printDate,
				logoBase64,
				sparcCards: props.sparcCards || [],
				frequencyLabel: normalizedFrequency,
			}),
		[printDate, logoBase64, props.sparcCards, normalizedFrequency],
	);

	const htmlContentOnePerPage = useMemo(
		() =>
			generateSparcKpiOnePerPageHTML({
				date: printDate,
				logoBase64,
				sparcCards: props.sparcCards || [],
				frequencyLabel: normalizedFrequency,
			}),
		[printDate, logoBase64, props.sparcCards, normalizedFrequency],
	);

	useImperativeHandle(
		ref,
		() => ({
			print: () => {
				if (Platform.OS === 'web') {
					const iframe = iframeRef.current;
					if (iframe?.contentWindow) {
						iframe.contentWindow.focus();
						iframe.contentWindow.print();
					}
					return;
				}

				// Mobile printing not required for now.
				console.log('Mobile print - use react-native-print with HTML:', htmlContent);
			},
			printOnePerPage: () => {
				if (Platform.OS === 'web') {
					const iframe = iframeOnePerPageRef.current;
					if (iframe?.contentWindow) {
						iframe.contentWindow.focus();
						iframe.contentWindow.print();
					}
					return;
				}

				console.log('Mobile print one-per-page - use react-native-print with HTML:', htmlContentOnePerPage);
			},
			getHtml: () => htmlContent,
		}),
		[htmlContent, htmlContentOnePerPage],
	);

	useEffect(() => {
		if (Platform.OS === 'web' && iframeRef.current?.contentDocument) {
			const doc = iframeRef.current.contentDocument;
			doc.open();
			doc.write(htmlContent);
			doc.close();
		}
	}, [htmlContent]);

	useEffect(() => {
		if (Platform.OS === 'web' && iframeOnePerPageRef.current?.contentDocument) {
			const doc = iframeOnePerPageRef.current.contentDocument;
			doc.open();
			doc.write(htmlContentOnePerPage);
			doc.close();
		}
	}, [htmlContentOnePerPage]);

	if (Platform.OS !== 'web') {
		return <View style={styles.hiddenContainer} />;
	}

	return (
		<View style={styles.hiddenContainer}>
			{/* @ts-ignore - iframe is web-only */}
			<iframe
				ref={iframeRef}
				style={{
					position: 'absolute',
					width: '1px',
					height: '1px',
					opacity: 0,
					pointerEvents: 'none',
				}}
				title="SPARC KPI Print"
			/>
			{/* @ts-ignore - iframe is web-only */}
			<iframe
				ref={iframeOnePerPageRef}
				style={{
					position: 'absolute',
					width: '1px',
					height: '1px',
					opacity: 0,
					pointerEvents: 'none',
				}}
				title="SPARC KPI Print One Per Page"
			/>
		</View>
	);
});

const styles = StyleSheet.create({
	hiddenContainer: {
		position: 'absolute',
		left: -9999,
		top: -9999,
		width: 1,
		height: 1,
		opacity: 0,
	},
});

SparcKpiListPrint.displayName = 'SparcKpiListPrint';
(SparcKpiListPrint as any).PureComponent = true;

export default SparcKpiListPrint;
